import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { gsap, ScrollTrigger, useScrollSmoother } from '@dboxdev/ui-components/gsap'
import { useAppLayout } from 'layouts/main'
import {
  Wrapper,
  HeroWrapper,
  HeroLogoMobile,
  HeroCopyMobile,
  HeroCopy,
  ArrowDown,
  FilmsWrapper,
  FilmsCol,
  FilmContent,
  FilmTitle,
  FilmCopy,
  FilmDescription,
  FilmImage,
  PlayIcon
} from 'styles/pages/index.styled'
import Menu from 'components/menu'
import { Hero } from '@dboxdev/ui-components'
import Logo from 'components/logo'
import FilmPopup from 'components/film-popup'
import Inquire from 'components/inquire'
import Footer from 'components/footer'
import { Seo } from 'components/seo'

const SCROLL = {
  duration: 1.5,
  ease: 'power2.inOut',
  threshold: 50
}

const IndexPage = ({ data: { heroPlaceholder, overviewImage, villaImage }, location }) => {
  const { mobile } = useAppLayout()
  const smoother = useScrollSmoother()
  const [heroVisible, setHeroVisible] = useState(true)
  const [heroPlaying, setHeroPlaying] = useState(false)
  const [openFilm, setOpenFilm] = useState(null)
  const [filmsVisible, setFilmsVisible] = useState(false)
  const [inquireEl, setInquireEl] = useState(null)
  const heroRef = useRef(null)
  const isSnappingRef = useRef(false)
  const explicitScrollRef = useRef(false)
  const nextSectionRef = useRef(null)

  const heroSettings = useMemo(() => (
    mobile
      ? {
          height: 35,
          video: 'https://vz-d1530224-ec5.b-cdn.net/6f226ce7-27df-4d03-8f60-883650bfe70d/playlist.m3u8'
        }
      : {
          height: 100,
          image: heroPlaceholder.childImageSharp,
          video: 'https://vz-d1530224-ec5.b-cdn.net/28b3eb06-1e0a-4343-8986-5ae0424ce293/video/1080p/video.m3u8'
        }
  ), [mobile, heroPlaceholder])

  const snapTo = useCallback((scrollTop) => {
    if (explicitScrollRef.current || isSnappingRef.current || !smoother.current) return
    const release = () => { isSnappingRef.current = false }

    isSnappingRef.current = true
    gsap.to(smoother.current, {
      scrollTop,
      duration: SCROLL.duration,
      ease: SCROLL.ease,
      overwrite: true,
      onComplete: release,
      onInterrupt: release
    })
  }, [smoother])

  const scrollToNextSection = useCallback(() => {
    if (!smoother.current || !nextSectionRef.current) return
    snapTo(smoother.current.offset(nextSectionRef.current))
  }, [smoother, snapTo])

  const scrollToInquire = useCallback(() => {
    if (!smoother.current || !inquireEl) return
    const release = () => { explicitScrollRef.current = false }

    explicitScrollRef.current = true
    gsap.to(smoother.current, {
      scrollTop: smoother.current.offset(inquireEl),
      duration: 1,
      overwrite: true,
      onComplete: release,
      onInterrupt: release
    })
  }, [smoother, inquireEl])

  useEffect(() => {
    if (location.hash !== '#inquire') return

    const timeout = setTimeout(scrollToInquire, 300)
    return () => clearTimeout(timeout)
  }, [location.hash, scrollToInquire])

  useEffect(() => {
    const hero = heroRef.current
    const next = nextSectionRef.current
    if (!hero || !next || !smoother.current) return

    const boundary = () => smoother.current.offset(next)
    const touch = ScrollTrigger.isTouch === 1

    let handedOver = false
    const syncToScroll = () => {
      if (handedOver) return
      const past = smoother.current.scrollTop() >= boundary() - 1
      setFilmsVisible(past)
      if (!touch) setHeroVisible(!past)
    }

    syncToScroll()

    const frame = window.requestAnimationFrame(syncToScroll)
    const timeouts = [300, 1000].map((delay) => window.setTimeout(syncToScroll, delay))
    const stopSeeding = () => {
      handedOver = true
      window.cancelAnimationFrame(frame)
      timeouts.forEach((timeout) => window.clearTimeout(timeout))
    }

    if (touch) {
      setHeroVisible(true)

      const trigger = ScrollTrigger.create({
        trigger: next,
        start: 'top 90%',
        onEnter: () => { stopSeeding(); setFilmsVisible(true) },
        onLeaveBack: () => { stopSeeding(); setFilmsVisible(false) }
      })

      return () => {
        stopSeeding()
        trigger.kill()
      }
    }

    const handover = (toFilms) => {
      stopSeeding()
      setFilmsVisible(toFilms)
      setHeroVisible(!toFilms)
      snapTo(toFilms ? boundary() : 0)
    }

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: hero,
        start: `top top-=${SCROLL.threshold}`,
        end: `bottom top+=${SCROLL.threshold}`,
        onEnter: () => handover(true),
        onEnterBack: () => handover(false)
      })
    })

    return () => {
      stopSeeding()
      ctx.revert()
    }
  }, [smoother, snapTo])

  useEffect(() => {
    if (!mobile) return

    const hero = heroRef.current
    if (!hero) return

    const start = () => {
      setHeroPlaying(true)
      window.clearInterval(poll)
      window.clearTimeout(fallback)
    }

    const poll = window.setInterval(() => {
      const host = hero.querySelector('hls-video, video')
      const media = host?.shadowRoot?.querySelector('video') || host
      if (media && !media.paused && media.currentTime > 0) start()
    }, 120)

    const fallback = window.setTimeout(start, 5000)

    return () => {
      window.clearInterval(poll)
      window.clearTimeout(fallback)
    }
  }, [mobile])

  const films = useMemo(() => [
    {
      id: 'overview',
      title: 'Overview Film',
      copy: [
        'THE FIRST PRIVATE RESIDENTIAL RESORT FROM FOUR SEASONS',
        'Now rising along a mile of private Lake Austin shoreline: a 210-acre, purely residential Four Seasons enclave. 100,000 sq. ft. of world-class resort amenities, including multiple clubhouses, marinas, pools, spa, sports facilities, and a 96-seat theater - paired with property-wide culinary experiences by Michelin-starred Chef Daniel Boulud. 20 minutes from downtown Austin.'
      ],
      image: overviewImage,
      video: 'https://vz-d1530224-ec5.b-cdn.net/85ddb0ff-a5af-4bd9-825e-7a117be7ff0d/playlist.m3u8'
    },
    {
      id: 'villa',
      title: 'Lake Villa Collection Film',
      copy: [
        '28 FREESTANDING HOMESITES',
        'Introducing the Lake Villa Collection at Four Seasons Private Residences Lake Austin—a limited collection of free-standing homes ranging from 5,500 to 6,000 square feet. Sited directly on and above Lake Austin, the Villas offer privacy, security, direct lake access, spectacular views, and Four Seasons legendary services.'
      ],
      image: villaImage,
      video: 'https://vz-d1530224-ec5.b-cdn.net/84472896-b15a-4750-80b9-3c11fedc2dcb/playlist.m3u8'
    }
  ], [overviewImage, villaImage])

  return (
    <Wrapper>
      <Menu inquireEl={inquireEl} onInquire={scrollToInquire} />
      <HeroWrapper ref={heroRef} $visible={heroVisible}>
        <HeroLogoMobile>
          <Logo />
        </HeroLogoMobile>
        <Hero {...heroSettings} />
        <HeroCopyMobile data-playing={heroPlaying}>
          <HeroCopy className='video-copy-1'>A 210 ACRE PRIVATE RESIDENTIAL ENCLAVE ON LAKE AUSTIN</HeroCopy>
          <HeroCopy className='video-copy-2'>WITH PRIVATE MARINAS<br />&amp; A LAKE CLUBHOUSE</HeroCopy>
          <HeroCopy className='video-copy-3'>100,000 SQUARE FEET<br />OF RESORT AMENITIES</HeroCopy>
          <HeroCopy className='video-copy-4'>INCLUDING<br /> ‘CAFé BOULUD LAKE AUSTIN’</HeroCopy>
          <HeroCopy className='video-copy-5'>20 MINUTES FROM<br /> DOWNTOWN AUSTIN</HeroCopy>
        </HeroCopyMobile>
        <ArrowDown onClick={scrollToNextSection}>
          <svg viewBox='0 0 76.4762 23.7505' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <line x1='0.483686' y1='0.875242' x2='40.2932' y2='22.8752' stroke='currentColor' strokeWidth='2' />
            <line x1='39.295' y1='22.893' x2='75.9617' y2='0.892993' stroke='currentColor' strokeWidth='2' />
          </svg>
        </ArrowDown>
      </HeroWrapper>
      <div ref={nextSectionRef} />
      <FilmsWrapper $visible={filmsVisible}>
        {films.map(({ id, title, copy, image }) => (
          <FilmsCol key={id} onClick={() => setOpenFilm(id)}>
            <FilmContent>
              <FilmTitle>{title}</FilmTitle>
              <FilmCopy>{copy[0]}</FilmCopy>
              <FilmDescription>{copy[1]}</FilmDescription>
            </FilmContent>
            <FilmImage><GatsbyImage image={getImage(image)} alt='' /></FilmImage>
            <PlayIcon>
              <svg viewBox='0 0 96.171 96.171' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <circle cx='48.0855' cy='48.0855' r='47.7516' fill='currentColor' fillOpacity='0.1' stroke='currentColor' strokeWidth='0.667854' />
                <path d='M70.7925 48.0858L35.7302 68.3291L35.7302 27.8426L70.7925 48.0858Z' fill='#D9D9D9' />
              </svg>
            </PlayIcon>
          </FilmsCol>
        ))}
      </FilmsWrapper>
      {openFilm && (
        <FilmPopup
          video={films.find(({ id }) => id === openFilm).video}
          title={films.find(({ id }) => id === openFilm).title}
          onClose={() => setOpenFilm(null)}
        />
      )}
      <div ref={setInquireEl} />
      <Inquire placement='home' />
      <Footer />
    </Wrapper>
  )
}

export default IndexPage

export const Head = ({ location }) => (
  <>
    <Seo location={location} />
    <script
      dangerouslySetInnerHTML={{
        __html: `
          (function () {
            var loader = document.createElement('script');
            loader.src = 'https://js.adsrvr.org/up_loader.1.1.0.js';
            loader.onload = function () {
              var init = function () {
                if (typeof TTDUniversalPixelApi !== 'function') return;
                new TTDUniversalPixelApi().init('wttn2f3', ['2btn52z'], 'https://insight.adsrvr.org/track/up');
              };
              if (typeof ttd_dom_ready === 'function') { ttd_dom_ready(init); } else { init(); }
            };
            document.head.appendChild(loader);
          })();
        `
      }}
    />
  </>
)

export const query = graphql`
  query HomeQuery {
    heroPlaceholder: file(relativePath: { eq: "home/hero.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    overviewImage: file(relativePath: { eq: "home/films/overview.jpg" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    villaImage: file(relativePath: { eq: "home/films/villa.jpg" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
  }
`
